import { InvoiceModel, InvoicesRepository, invoicesState } from '@/Invoices/domain'
import {
  FilterResultModel,
  HttpMethod,
  HttpModel,
  IFile,
  PaginationModel,
  convertFiltersToQuery,
  edenredProducts,
} from '@/Shared/domain'
import {
  axiosFetch,
  createQueryArrayToString,
} from '@/Shared/infrastructure'
import {
  downloadGuarderiaTransaction,
  downloadInvoice,
  downloadRestaurantTransaction,
  getInvoiceData,
} from '@/Shared/infrastructure/endpoints'

export function createApiInvoiceRepository(): InvoicesRepository {
  return new ApiInvoiceRepository()
}

type ApiInvoiceModel = {
  merchantId: number
  merchantName: string
  merchantParentId: number | null
  hasMerchantHardDependency: boolean
  masterGroupId: number | null
  masterGroupAlias: string | null
  superMasterGroupAlias: string | null
  invoiceId: number
  invoiceNumber: string
  productCode: edenredProducts.ticketRestaurant | edenredProducts.ticketGuarderia
  productName: string
  invoiceCreationDate: string
  invoiceOperationDate: string
  invoiceLimitDate: string
  reimburcementInitialDate: string
  reimburcementEndDate: string
  invoiceTotalAmount: number
  invoiceBalanceDue: number
  reimburcementQuantity: number
  reimburcementAmount: number
  invoiceStatus: invoicesState
  invoiceNatureId: number
  invoiceTypeId: number
  isInvoiceCorrective: boolean
  isInvoiceIsDownloadable: boolean
  isInvoiceDownloadFileReady: boolean
  isInvoinceTransactionReportDownloadable: boolean
}

type ApiInvoices = {
  rows: ApiInvoiceModel[]
  totalRows: number
}

const convertToDomain = (response: ApiInvoices): PaginationModel<InvoiceModel> => {
  if (!response?.rows) {
    return {
      rows: [],
      totalRows: 0,
    }
  }

  return {
    rows: response.rows.map(
      (invoice): InvoiceModel => ({
        issueDate: invoice.invoiceCreationDate,
        merchantName: invoice.merchantName,        
        invoiceNumber: invoice.invoiceNumber,
        state: invoice.invoiceStatus,
        firstTransactionDate: invoice.reimburcementInitialDate,
        lastTransactionDate: invoice.reimburcementEndDate,
        numberOfTransactions: invoice.reimburcementQuantity,
        generatedAmount: invoice.reimburcementAmount,
        totalAmount: invoice.invoiceTotalAmount,
        invoiceId: invoice.invoiceId,
        isInvoiceDownloadable: invoice.isInvoiceIsDownloadable,
        isInvoiceReadyToDownload: invoice.isInvoiceDownloadFileReady,
        isTransactionReadyToDownload: invoice.isInvoinceTransactionReportDownloadable,
        productCode: invoice.productCode,
      }),
    ),
    totalRows: response.totalRows,
  }
}

export class ApiInvoiceRepository implements InvoicesRepository {
  async GetInvoicesData(
    pageNumber: number,
    numberOfRecordsPerPage: number,
    search?: string,
    filters?: FilterResultModel[],
  ): Promise<HttpModel<PaginationModel<InvoiceModel>>> {
    const merchantList = (filters || [])
      .filter(f => f.name === 'merchantFilterList' || f.name === 'localCode')
      .flatMap(f =>
        f.name === 'localCode'
          ? (f.value || '')
              .split(',')
              .map(v => v.trim())
              .filter(Boolean)
          : [f.value],
      )
    const otherFilters = (filters || []).filter(
      f => f.name !== 'merchantFilterList' && f.name !== 'localCode',
    )

    const convertedFilters = (convertFiltersToQuery(otherFilters) || {}) as Record<
      string,
      unknown
    >
    const { invoiceStatus, ...restFilters } = convertedFilters
    const parsedInvoiceStatus =
      invoiceStatus !== undefined && invoiceStatus !== ''
        ? Number(invoiceStatus)
        : undefined

    const bodyData = {
      multiFilter: search ?? '',
      ...(merchantList.length > 0 ? { merchantFilterList: merchantList } : {}),
      ...restFilters,
      paginationModel: {
        numberOfRecordsPerPage: numberOfRecordsPerPage,
        pageNumber: pageNumber - 1,
        orderByField: '',
        sortDescending: true,
      },
      ...(parsedInvoiceStatus !== undefined && !Number.isNaN(parsedInvoiceStatus)
        ? { invoiceStatus: parsedInvoiceStatus }
        : {}),
    }

    const response = await axiosFetch(
      getInvoiceData,
      HttpMethod.POST,
      bodyData,
    )

    return {
      data: convertToDomain(response.data),
      meta: response.meta,
    }
  }

  async DownloadInvoice(invoicesIds: number[]): Promise<HttpModel<IFile[]>> {
    const invoicesIdsString: string[] = invoicesIds.map(id => id.toString())

    const response = await axiosFetch(
      downloadInvoice.concat(
        createQueryArrayToString(invoicesIdsString, 'invoiceIdList'),
      ),
      HttpMethod.GET,
      {},
    )

    return {
      data: response.data ?? null,
      meta: response.meta,
    }
  }

  async DownloadTransaction(
    invoicesIds: number[],
    productType: edenredProducts.ticketRestaurant | edenredProducts.ticketGuarderia,
  ): Promise<HttpModel<IFile>> {
    const pt =
      productType === edenredProducts.ticketGuarderia
        ? downloadGuarderiaTransaction
        : downloadRestaurantTransaction

    const invoicesIdsString: string[] = invoicesIds.map(id => id.toString())

    const response = await axiosFetch(
      pt.concat(createQueryArrayToString(invoicesIdsString, 'invoiceIds')),
      HttpMethod.GET,
      {},
    )

    return {
      data: response.data ?? null,
      meta: response.meta,
    }
  }
}
